import React, { useMemo } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import type { Image, ImageSet } from 'api/api.types'
import { AppLink } from 'components/Utility/AppLink/AppLink'
import { RawPicture } from './RawPicture'

type PictureProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  desktopType: string,
  mobileType: string,
  image: ImageSet | null | undefined,
  link?: string | null,
  className?: string,
  imageClassName?: string,
  lazy?: boolean,
  /** Adds <link rel="preload"> for the images (first screen) */
  preload?: boolean,
  onClick?: () => void,
}

const PreloadLinks: React.FC<{ images: Array<Image | null> }> = ({ images }) => (
  <Helmet>
    {images.map((img) => img && (
      <link key={img.path} rel={'preload'} as={'image'} href={encodeURI(img.path)} />
    ))}
  </Helmet>
)

/** Picks the desktop, mobile and mobile low-res variants of a CMS image set by type */
export const Picture: React.FC<PictureProps> = (props) => {
  const {
    desktopType, mobileType, link, image, className, imageClassName, lazy, onClick, preload, ...rest
  } = props

  const [desktopImage, mobileImage, mobileLow] = useMemo(() => {
    let desktop: Image | null = null
    let mobile: Image | null = null
    let low: Image | null = null
    for (const img of image ?? []) {
      if (img.type === desktopType) {
        desktop = img
      } else if (img.type === mobileType) {
        mobile = img
      } else if (img.type === mobileType + '-lowres') {
        low = img
      }
    }
    return [desktop, mobile, low]
  }, [image, desktopType, mobileType])

  if (!desktopImage && !mobileImage) {
    return (
      <div className={className}>
        <div className={'empty-image'} onClick={onClick} />
      </div>
    )
  }

  const picture = (
    <RawPicture
      mobileImage={mobileImage}
      mobileLow={mobileLow}
      desktopImage={desktopImage}
      className={imageClassName}
      lazy={lazy}
      {...rest}
      onClick={onClick}
    />
  )

  return (
    <div className={className}>
      {preload && <PreloadLinks images={[mobileLow, mobileImage, desktopImage]} />}
      {link ? <AppLink path={link}>{picture}</AppLink> : picture}
    </div>
  )
}
